import OverlayType from '@Shared.Angular/@types/core/contracts/queryModel/procedure/overlayType';
import IProcedureActionOverlayDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionOverlayDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import React, { useState, useRef, useEffect } from 'react';

interface ProcedureActionOverlayProps {
  minWidth?: number;
  minHeight?: number;
  scale: number;
  overlay: IProcedureActionOverlayDetail;
  containerRef: React.RefObject<HTMLDivElement>;
  onOverlayUpdated: (
    id: Guid,
    x: number,
    y: number,
    width: number,
    height: number
  ) => void;
  onOverlayDeleted: (id: Guid) => void;
}

const ProcedureActionOverlay: React.FC<ProcedureActionOverlayProps> = ({
  overlay,
  scale,
  minWidth = 20,
  minHeight = 20,
  containerRef,
  onOverlayUpdated,
  onOverlayDeleted
}) => {
  const [position, setPosition] = useState({
    x: overlay.x * scale,
    y: overlay.y * scale
  });
  const [size, setSize] = useState({
    width: overlay.width * scale,
    height: overlay.height * scale
  });
  useEffect(() => {
    const newPosition = {
      x: overlay.x * scale,
      y: overlay.y * scale
    };
    const newSize = {
      width: overlay.width * scale,
      height: overlay.height * scale
    };
    setPosition(newPosition);
    setSize(newSize);
  }, [overlay, scale]);

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const resizeStartSize = useRef({ width: 0, height: 0 });
  const resizeStartPos = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent default to avoid text selection during drag
    e.preventDefault();

    setIsDragging(true);

    dragStartPos.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  const handleResizeStart = (e: React.MouseEvent, direction: string) => {
    // Prevent default to avoid text selection during resize
    e.preventDefault();
    e.stopPropagation();

    setIsResizing(true);
    setResizeDirection(direction);

    resizeStartSize.current = { ...size };
    resizeStartPos.current = { ...position };
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY
    };
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragStartPos.current.x;
      const newY = e.clientY - dragStartPos.current.y;

      // Ensure the box stays within the container
      if (containerRef.current && overlayRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const overlayRect = overlayRef.current.getBoundingClientRect();

        const maxX = containerRect.width - overlayRect.width;
        const maxY = containerRect.height - overlayRect.height;
        const thresholdPixels = 5; // Stop the overlay extending beyond the container
        setPosition({
          x: Math.max(thresholdPixels, Math.min(newX, maxX - thresholdPixels)),
          y: Math.max(thresholdPixels, Math.min(newY, maxY - thresholdPixels))
        });
      } else {
        setPosition({ x: newX, y: newY });
      }
    } else if (isResizing && resizeDirection) {
      const deltaX = e.clientX - dragStartPos.current.x;
      const deltaY = e.clientY - dragStartPos.current.y;

      let newWidth = resizeStartSize.current.width;
      let newHeight = resizeStartSize.current.height;
      let newX = resizeStartPos.current.x;
      let newY = resizeStartPos.current.y;

      if (resizeDirection.includes('e')) {
        newWidth = Math.max(minWidth, resizeStartSize.current.width + deltaX);
      }
      if (resizeDirection.includes('s')) {
        newHeight = Math.max(
          minHeight,
          resizeStartSize.current.height + deltaY
        );
      }
      if (resizeDirection.includes('w')) {
        const widthChange =
          resizeStartSize.current.width -
          Math.max(minWidth, resizeStartSize.current.width - deltaX);
        newWidth = Math.max(minWidth, resizeStartSize.current.width - deltaX);
        newX = resizeStartPos.current.x + widthChange;
      }
      if (resizeDirection.includes('n')) {
        const heightChange =
          resizeStartSize.current.height -
          Math.max(minHeight, resizeStartSize.current.height - deltaY);
        newHeight = Math.max(
          minHeight,
          resizeStartSize.current.height - deltaY
        );
        newY = resizeStartPos.current.y + heightChange;
      }

      if (containerRef.current) {
        const thresholdPixels = 12;
        const maxWidth = containerRef.current.offsetWidth - thresholdPixels;
        const maxHeight = containerRef.current.offsetHeight - thresholdPixels;
        newWidth = Math.min(newWidth, maxWidth);
        newHeight = Math.min(newHeight, maxHeight);
      }

      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const inverseScale = 1 / scale;
      onOverlayUpdated(
        overlay.id,
        Math.floor(position.x * inverseScale),
        Math.floor(position.y * inverseScale),
        Math.floor(size.width * inverseScale),
        Math.floor(size.height * inverseScale)
      );
    }, 300);

    return () => clearTimeout(timeout);
  }, [position, size]);

  return (
    scale > 0 && (
      <div
        ref={overlayRef}
        className={`overlay-box ${
          overlay.type === OverlayType.Redaction ? 'redaction' : ''
        } ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
        style={{
          width: `${size.width}px`,
          height: `${size.height}px`,
          top: `${position.y}px`,
          left: `${position.x}px`
        }}
        onMouseDown={handleMouseDown}
      >
        <div
          className="overlay-resize-handle overlay-resize-n"
          onMouseDown={(e) => handleResizeStart(e, 'n')}
        ></div>
        <div
          className="overlay-resize-handle overlay-resize-ne"
          onMouseDown={(e) => handleResizeStart(e, 'ne')}
        ></div>
        <div
          className="overlay-resize-handle overlay-resize-e"
          onMouseDown={(e) => handleResizeStart(e, 'e')}
        ></div>
        <div
          className="overlay-resize-handle overlay-resize-se"
          onMouseDown={(e) => handleResizeStart(e, 'se')}
        ></div>
        <div
          className="overlay-resize-handle overlay-resize-s"
          onMouseDown={(e) => handleResizeStart(e, 's')}
        ></div>
        <div
          className="overlay-resize-handle overlay-resize-sw"
          onMouseDown={(e) => handleResizeStart(e, 'sw')}
        ></div>
        <div
          className="overlay-resize-handle overlay-resize-w"
          onMouseDown={(e) => handleResizeStart(e, 'w')}
        ></div>
        <div
          className="overlay-resize-handle overlay-resize-nw"
          onMouseDown={(e) => handleResizeStart(e, 'nw')}
        ></div>
        <i
          className="fa-light fa-times"
          title="Delete"
          onClick={() => onOverlayDeleted(overlay.id)}
        ></i>
      </div>
    )
  );
};

export default ProcedureActionOverlay;
